import { useState, useEffect, useCallback } from 'react';
import { IItemPedidoKDS, StatusItemKDS } from '../interfaces/IKDS';

interface NavPedido {
    pedidoUuid: string;
    itens: IItemPedidoKDS[];
}

interface Callbacks {
    onIniciarItem: (uuid: string) => void;
    onConcluirItem: (uuid: string) => void;
    onReverterItem?: (uuid: string) => void;
    onConcluirTodos?: (pedidoUuid: string) => void;
    onLiberarPedido?: (pedidoUuid: string) => void;
}

export const useKDSKeyboardNavigation = (pedidos: NavPedido[], callbacks: Callbacks) => {
    const [selectedPedidoIndex, setSelectedPedidoIndex] = useState(0);
    const [selectedItemIndex, setSelectedItemIndex] = useState(0);

    // Ajusta índices quando a lista muda
    useEffect(() => {
        setSelectedPedidoIndex(prev => Math.min(prev, Math.max(0, pedidos.length - 1)));
    }, [pedidos.length]);

    useEffect(() => {
        const pedido = pedidos[selectedPedidoIndex];
        if (pedido) {
            setSelectedItemIndex(prev => Math.min(prev, Math.max(0, pedido.itens.length - 1)));
        }
    }, [selectedPedidoIndex, pedidos]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!pedidos.length) return;

        // Ignora quando foco está em input/textarea
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

        const tecla = e.key;
        const isNumpad = e.code?.startsWith('Numpad');

        // Navegação horizontal entre pedidos (cards)
        if (tecla === 'ArrowRight' || (isNumpad && tecla === '6')) {
            setSelectedPedidoIndex(i => Math.min(i + 1, pedidos.length - 1));
            setSelectedItemIndex(0);
            e.preventDefault();
            return;
        }
        if (tecla === 'ArrowLeft' || (isNumpad && tecla === '4')) {
            setSelectedPedidoIndex(i => Math.max(i - 1, 0));
            setSelectedItemIndex(0);
            e.preventDefault();
            return;
        }

        // Navegação vertical entre itens
        const pedidoAtual = pedidos[selectedPedidoIndex];
        if (!pedidoAtual) return;

        if (tecla === 'ArrowDown' || (isNumpad && tecla === '2')) {
            setSelectedItemIndex(i => Math.min(i + 1, pedidoAtual.itens.length - 1));
            e.preventDefault();
            return;
        }
        if (tecla === 'ArrowUp' || (isNumpad && tecla === '8')) {
            setSelectedItemIndex(i => Math.max(i - 1, 0));
            e.preventDefault();
            return;
        }

        // Enter: avançar item selecionado
        if (tecla === 'Enter') {
            const item = pedidoAtual.itens[selectedItemIndex];
            if (!item) return;
            if (item.status === StatusItemKDS.AGUARDANDO) {
                callbacks.onIniciarItem(item.uuid);
            } else if (item.status === StatusItemKDS.EM_PREPARO) {
                callbacks.onConcluirItem(item.uuid);
            } else if (item.status === StatusItemKDS.PRONTO) {
                const todosProtos = pedidoAtual.itens
                    .filter(i => i.status !== StatusItemKDS.CANCELADO && i.status !== StatusItemKDS.LIBERADO)
                    .every(i => i.status === StatusItemKDS.PRONTO);
                if (todosProtos) {
                    callbacks.onLiberarPedido?.(pedidoAtual.pedidoUuid);
                } else {
                    callbacks.onConcluirTodos?.(pedidoAtual.pedidoUuid);
                }
            }
            e.preventDefault();
            return;
        }

        // Backspace: reverter item selecionado
        if (tecla === 'Backspace' && callbacks.onReverterItem) {
            const item = pedidoAtual.itens[selectedItemIndex];
            if (!item) return;
            if (item.status === StatusItemKDS.EM_PREPARO || item.status === StatusItemKDS.PRONTO) {
                callbacks.onReverterItem(item.uuid);
            }
            e.preventDefault();
            return;
        }
    }, [pedidos, selectedPedidoIndex, selectedItemIndex, callbacks]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const selectedPedidoUuid = pedidos[selectedPedidoIndex]?.pedidoUuid;
    const selectedItemUuid = pedidos[selectedPedidoIndex]?.itens[selectedItemIndex]?.uuid;

    return { selectedPedidoUuid, selectedItemUuid };
};
